import { AttendanceGuage, Button, MainContainer, TabBar, TabItem } from '@wot/components';
import { useClickOutside } from '@wot/shared';
import { ITrialClass } from '@wot/typings';
import { formatInTimeZone } from 'date-fns-tz';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { plansApi } from '../../api/plan';
import { sessionsApi } from '../../api/sessions';
import { DEFAULT_PROFILE_PIC, MORE_SVG, PLUS_ICON, PRO } from '../../assets/icons';
import { useAppSelector } from '../../hooks';
import useCurrency from '../../hooks/useCurrency';
import { useTab } from '../../hooks/useTab';
import { LPlan } from '../../interfaces/Plan';
import { getAttendacePercentage } from '../../utils/functions';
import BookSessions from './BookSession';
import CancelPlan from './CancelPlan';
import ChangeRequest from './ChangeRequest';
import ExtendPlan from './ExtendPlan';
import { LOADING } from '../../assets/animated';
import { SEO } from '../../components/SEO';

const capitalizeFirstLetter = (string: string) => string.charAt(0).toUpperCase() + string.slice(1);

export default function MyPlans() {
  const { activeTab, onTabChange } = useTab('all');
  const { userData } = useAppSelector((state) => state.userSlice);
  const [plans, setPlans] = useState<LPlan[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userData?._id) {
      setLoading(true);
      plansApi.get(userData?._id).then((data) => {
        setPlans(data);
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [activeTab]);

  return (
    <>
    <SEO 
      title="My Plans - Tutoring Plans"
      description="Manage your tutoring plans, view active subscriptions, and track your learning progress."
      keywords="my plans, tutoring plans, subscriptions, learning plans"
      url="/my-plans"
      type="website"
      noIndex={true}
    />
    <MainContainer>
      <div className="w-full h-full flex flex-col bg-white rounded-md">
        <h1 className="text-2xl font-semibold p-4">My Plans</h1>
        <div className="w-full border-b border-black">
          <div className="px-4">
            <TabBar
              value={activeTab}
              indicatorColor="bg-secondary"
              onChange={onTabChange}
              textColor="text-neutralGray1"
            >
              <TabItem label="All Plans" value="all" fontSize={15} />
              <TabItem label="Active" value="active" />
              <TabItem label="Pending" value="pending" />
              <TabItem label="Completed" value="completed" />
              <TabItem label="Cancelled" value="cancelled" />
            </TabBar>
          </div>
        </div>
        {loading ?
          <div className="w-full min-h-[512px] flex items-center justify-center">
            <img src={LOADING} className="w-14 h-14" />
          </div>
          :
          <div className="p-6 flex flex-col gap-4 items-center">
            {activeTab === 'all' ? (
              plans.length === 0 ? (
                <div className="w-full flex flex-col items-center justify-center gap-7 py-10">
                  <h1 className="text-2xl font-semibold text-center">You haven&apos;t purchased any Plans yet!</h1>
                  <Link to={'/find-tutor'}>
                    <Button text="Buy Plan Now" type="primary" />
                  </Link>
                </div>
              ) : (
                plans.map((plan) => <PlanCard key={plan._id} plan={plan} />)
              )
            ) : plans.filter((plan) => plan.status === activeTab).length === 0 ? (
              <div className="w-full flex flex-col items-center justify-center gap-7 py-10">
                <h1 className="text-2xl font-semibold text-center">{`No ${activeTab} plans yet!`}</h1>
              </div>
            ) : (
              plans.filter((plan) => plan.status === activeTab).map((plan) => <PlanCard key={plan._id} plan={plan} />)
            )}
          </div>

        }
      </div>
    </MainContainer>
    </>
  );
}

interface Props {
  plan: LPlan;
}
export function PlanCard({ plan: iplan }: Props) {
  const [plan, setPlan] = useState(iplan);
  const [sessions, setSessions] = useState<ITrialClass[]>([]);
  const { timezone } = useAppSelector((state) => state.preferencesSlice);
  const navigate = useNavigate();
  const convertPrice = useCurrency();
  const [bookSessionsPortal, setBookSessionsPortal] = useState(false);
  const { userData } = useAppSelector((state) => state.userSlice);
  const [viewMoreOpt, setViewMoreOpt] = useState(false);
  const viewMoreRef = useRef(null);
  const [showExtendPlan, setShowExtendPlan] = useState<boolean>(false);
  const [showChangeRequest, setShowChangeRequest] = useState<boolean>(false);
  const [showCancelPlan, setShowCancelPlan] = useState<boolean>(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const id = searchParams.get("id");
    const bookSession = searchParams.get("bookSession");
    if (id === plan._id && bookSession) {
      setBookSessionsPortal(true);
    }
  }, [searchParams]);

  useClickOutside(viewMoreRef, () => setViewMoreOpt(false));

  useEffect(() => {
    if (userData?._id) {
      sessionsApi.get(userData._id, { planId: plan._id }).then((data) => {
        setSessions(data);
      });
    }
  }, []);

  const onCancelPlan = async () => {
    return await plansApi
      .cancelPlan(plan._id)
      .then(() => {
        setPlan({
          ...plan,
          status: 'cancelled'
        });
        setShowCancelPlan(false);
        toast.success('Plan cancelled');
        return true;
      })
      .catch((err) => {
        toast.error('something went wrong.');
        throw err;
      });
  };

  const onChangeTutorRequest = async (reason: string) => {
    return await plansApi
      .changeTutorRequest(plan._id, reason)
      .then(() => {
        setPlan({
          ...plan,
          changeRequest: {
            fulfilled: false,
            status: 'pending',
            reason,
            createdAt: new Date().toDateString(),
            updatedAt: new Date().toDateString()
          }
        });
        setShowChangeRequest(false);
        toast.success('Request sent successfully');
        return true;
      })
      .catch((err) => {
        toast.success('Request failed');
        throw err;
      });
  };

  function ViewMore() {
    return (
      <div
        ref={viewMoreRef}
        className="flex flex-col absolute top-4 right-5 bg-white drop-shadow-lg py-2 px-1 rounded-lg cursor-pointer"
      >
        <span
          className="py-2 px-2 text-sm rounded-md hover:bg-neutralBlue2"
          onClick={() => {
            plan.changeRequest
              ? toast.error('You have already requested tutor change for this plan')
              : setShowChangeRequest(true);
          }}
        >
          Request for Tutor Change
        </span>
        {/* <span className="py-2 px-2 text-sm rounded-md hover:bg-neutralBlue2">Report an issue</span> */}
      </div>
    );
  }

  return (
    <div
      className={`w-full max-w-[1250px] relative flex flex-col md:flex-row gap-4 rounded-lg border-primary border-l-4 overflow-hidden border`}
    >
      <div className="w-full flex flex-col gap-7 px-4 md:px-7 py-4">
        <div>
          <div className="w-max bg-neutralBlue2 p-2 rounded-md">
            <p className="text-sm font-semibold">Plan ID: {plan._id}</p>
          </div>
          <div className="w-max py-1 flex items-center gap-2">
            <p className="text-sm text-neutralGray1">Plan Status: </p>
            <span className="rounded-full border text-xs font-medium px-3 py-1">{plan.status}</span>
          </div>
          <div className="w-max py-1 flex items-center gap-2">
            <p className="text-sm text-neutralGray1">Plan Category: </p>
            <span className="text-xs font-medium py-1">{capitalizeFirstLetter(plan.categoryType)}</span>
          </div>
          <div className="w-max py-1 flex items-center gap-2">
            <p className="text-sm text-neutralGray1">{`Plan ${plan.categoryType === 'subject' ? 'Subjects' : capitalizeFirstLetter(plan.categoryType)
              }:`}</p>
            <span className="text-xs font-medium py-1">{plan.category.name}</span>
          </div>
        </div>

        {plan.status !== 'cancelled' && (
          <div className="block md:hidden w-full mt-auto pb-4 border-b border-primary">
            <div className="w-full max-w-sm mx-auto flex gap-4 items-center justify-center">
              {/* TODO: Show cancel button only when the plan is active */}
              <Button
                block
                text="Cancel"
                type="s-outlined"
                onClick={() => {
                  if (userData?._id) {
                    setShowCancelPlan(true);
                  } else {
                    // @ts-ignore
                    localStorage.setItem('prevPath', window.location.pathname);
                    navigate('/login');
                  }
                }}
              />
              {plan.status === 'active' && (
                <Button
                  block
                  text="Extend Plan"
                  onClick={() => {
                    if (userData?._id) {
                      setShowExtendPlan(true);
                    } else {
                      // @ts-ignore
                      localStorage.setItem('prevPath', window.location.pathname);
                      navigate('/login');
                    }
                  }}
                />
              )}
            </div>
          </div>
        )}

        <div className="w-full flex flex-col md:flex-row md:flex-nowrap gap-4 md:gap-7">
          <div className="flex flex-wrap gap-4 md:gap-7">
            <div className="flex flex-col gap-2">
              <p className="text-lg md:text-3xl font-bold text-primary">
                {sessions.filter((s) => s.status === 'completed').length}/{plan.totalSessions}
              </p>
              <p className="text-sm text-neutralGray2 font-medium">Completed Classes</p>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-lg md:text-3xl font-bold text-primary">
                {plan.totalSessions - plan.purchasedSessions || 0}
              </p>
              <p className="text-sm text-neutralGray2 font-medium">Complimentary classes </p>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-lg md:text-3xl font-bold text-primary">{plan.totalSessions - sessions.filter((s) => s.status === "completed" || s.status === "upcoming").length}</p>
              <p className="text-sm text-neutralGray2 font-medium">Remaining classes</p>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-lg md:text-3xl font-bold text-primary">
                {sessions.filter((s) => s.status === 'upcoming').length}
              </p>
              <p className="text-sm text-neutralGray2 font-medium">Upcoming classes</p>
            </div>
            <div className="flex flex-wrap gap-7">
              <div className="flex flex-col gap-2">
                <p className="text-lg md:text-3xl font-bold text-primary">
                  {convertPrice(plan.totalCost).amountString}
                </p>
                <p className="text-sm text-neutralGray2 font-medium">COST</p>
              </div>

              <div className="flex flex-col gap-2">
                <p className="text-lg md:text-3xl font-bold text-primary">
                  {formatInTimeZone(plan.createdAt, timezone, 'd LLL y')}
                </p>
                <p className="text-sm text-neutralGray2 font-medium">Purchase Date</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-base font-semibold text-neutralGray2">ATTENDANCE</p>
            <div className="flex flex-row md:flex-col items-center gap-4">
              <div className="w-16 h-16 md:w-24 md:h-24 shrink-0">
                <AttendanceGuage
                  percentage={getAttendacePercentage(
                    sessions.filter((i) => i.attendance && i.attendance.student === 'present').length,
                    plan.totalSessions
                  )}
                />
              </div>
              <Link to={`/plans/${plan._id}/attendance`}>
                <p className="text-sm text-primary font-medium shrink-0 whitespace-nowrap underline underline-offset-1">
                  View detailed Attendance
                </p>
              </Link>
            </div>
          </div>
        </div>
        {plan.status !== 'cancelled' && plan.status === 'active' && (
          <div className="hidden md:block w-full mt-auto pt-4 border-t border-primary">
            <div className="w-full max-w-sm mx-auto flex gap-4 items-center justify-center">
              <Button
                block
                text="Cancel"
                type="s-outlined"
                onClick={() => {
                  if (userData?._id) {
                    setShowCancelPlan(true);
                  } else {
                    // @ts-ignore
                    localStorage.setItem('prevPath', window.location.pathname);
                    navigate('/login');
                  }
                }}
              />
              <Button
                block
                text="Extend Plan"
                onClick={() => {
                  if (userData?._id) {
                    setShowExtendPlan(true);
                  } else {
                    // @ts-ignore
                    localStorage.setItem('prevPath', window.location.pathname);
                    navigate('/login');
                  }
                }}
              />
            </div>
          </div>
        )}
      </div>
      {plan.tutor && (
        <div className="w-full md:w-max flex md:shrink-0">
          <div className="w-full h-full md:w-[300px] flex flex-col border border-neutralGray3 p-4 rounded-lg cursor-pointer bg-neutralBlue2">
            <div className="flex justify-end items-center gap-2">
              {plan.tutor.level === 'premium' && <img src={PRO} alt="Pro badge" className="h-7 w-7" />}
              {plan.status === 'active' && (
                <div className="w-full relative flex justify-end">
                  <img
                    src={MORE_SVG}
                    alt="More options"
                    className="h-4 w-4 cursor-pointer"
                    onClick={() => setViewMoreOpt(!viewMoreOpt)}
                  />
                  {viewMoreOpt && <ViewMore />}
                </div>
              )}
            </div>
            <div className="h-full flex flex-col gap-4 items-start md:items-center md:px-10 py-4">
              <Link to={`/find-tutor/${plan.tutorId}`} className="w-full">
                <div className="flex md:flex-col items-center gap-2">
                  <div className="h-10 w-10 md:h-16 md:w-16 rounded-full overflow-hidden">
                    <img
                      src={plan.tutor.profilePic || DEFAULT_PROFILE_PIC}
                      alt="tutor_profile_pic"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="text-2xl font-normal">{plan.tutor.name}</span>
                </div>
              </Link>

              {plan.status === 'active' && (
                <div className="w-full max-w-sm mx-auto mt-auto flex flex-col gap-4 items-center justify-center whitespace-nowrap">
                  <Button
                    block
                    text="Open Calender"
                    type="s-outlined"
                    onClick={() => setBookSessionsPortal(true)}
                    size="sm"
                  />
                  <Button
                    onClick={() => navigate('/inbox', { state: { ...plan.tutor } })}
                    block
                    text="Message"
                    type="secondary"
                    size="sm"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {bookSessionsPortal && (
        <BookSessions
          setBookSessionsPortal={setBookSessionsPortal}
          planId={plan._id}
          tutorId={plan.tutorId}
          userId={plan.userId}
          category={plan.category}
          categoryType={plan.categoryType}
          remainingSessions={plan.totalSessions - sessions.filter((s) => s.status === "completed" || s.status === "upcoming").length}
          completedSessions={sessions.filter((s) => s.status === 'completed' || s.status === 'upcoming').length}
          totalSessions={plan.totalSessions}
        />
      )}
      {showExtendPlan && <ExtendPlan plan={plan} setShowExtendPlan={setShowExtendPlan} />}
      {showChangeRequest && (
        <ChangeRequest
          plan={plan}
          setShowChangeRequest={setShowChangeRequest}
          onChangeTutorRequest={onChangeTutorRequest}
        />
      )}
      {showCancelPlan && <CancelPlan plan={plan} onCancelPlan={onCancelPlan} setShowCancelPlan={setShowCancelPlan} />}
    </div>
  );
}

export function PlanCard2() {
  const categories = [{ name: 'maths' }];
  return (
    <div
      className={`w-full max-w-[1250px] relative flex flex-col md:flex-row gap-4 rounded-lg border-primary border-l-4 overflow-hidden border`}
    >
      <div className="w-full flex flex-col gap-7 px-7 py-4">
        <div className="flex items-center gap-4">
          <div className="w-max bg-neutralBlue2 p-2 rounded-md">
            <p className="text-sm font-semibold">Plan ID: 12345778</p>
          </div>
          <div className="w-max flex items-center gap-2">
            <p className="text-base text-neutralGray1">Payment Status: </p>
            <span className="rounded-full border bg-success text-green-600 text-xs font-medium px-3 py-1">Paid</span>
          </div>
        </div>
        <div className="block md:hidden w-full mt-auto pb-4 border-b border-primary">
          <div className="w-full max-w-sm mx-auto flex gap-4 items-center justify-center">
            <Button block text="Cancel" type="s-outlined" />
            <Button block text="Extend" />
          </div>
        </div>

        <div className="w-full flex flex-col md:flex-row md:flex-nowrap gap-7">
          <div className="flex flex-wrap gap-7">
            <div className="flex flex-col gap-2">
              <p className="text-3xl font-bold text-primary">14/24</p>
              <p className="text-sm text-neutralGray2 font-medium">
                Completed <br />
                Classes
              </p>
            </div>

            {/* <div className="flex flex-col gap-2">
              <p className="text-3xl font-bold text-primary">14/24</p>
              <p className="text-sm text-neutralGray2 font-medium">Complimentary classes </p>
            </div> */}
            <div className="flex flex-col items-center gap-2">
              <img src={PLUS_ICON} className="w-10 h-10" />
              <p className="text-sm text-neutralGray2 font-medium">
                Complimentary <br />
                classes{' '}
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-3xl font-bold text-primary">14/24</p>
              <p className="text-sm text-neutralGray2 font-medium">
                Remaining <br />
                classes
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-3xl font-bold text-primary">14/24</p>
              <p className="text-sm text-neutralGray2 font-medium">
                Upcoming <br />
                classes
              </p>
            </div>
            <div className="flex flex-wrap gap-7">
              <div className="flex flex-col gap-2">
                <p className="text-base font-semibold text-neutralGray2">COST</p>
                <p className="text-lg text-black font-bold">INR 24000</p>
              </div>

              <div className="flex flex-col gap-2">
                <p className="text-base font-semibold text-neutralGray2">Purchase Date</p>
                <p className="text-lg text-black font-bold">21 Sept 2022</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-base font-semibold text-neutralGray2">ATTENDANCE</p>
            <div className="w-24 h-24">
              <AttendanceGuage percentage={23} />
            </div>
            <p className="text-sm text-primary font-medium shrink-0 whitespace-nowrap">View detailed Attendance</p>
          </div>
        </div>
      </div>

      <div className="w-full md:w-max flex md:shrink-0">
        <div className="w-full flex flex-col border border-neutralGray3 p-4 rounded-lg cursor-pointer bg-neutralBlue2">
          <div className="w-full flex items-center justify-center">
            <p className="text-sm underline">Previously Assigned Tutor</p>
          </div>
          <div className="flex justify-end">
            <img src={PRO} alt="Pro badge" className="h-7 w-7" />
          </div>
          <div className="flex flex-col gap-4 items-start md:items-center py-4">
            <div className="flex md:flex-col items-center gap-2">
              <div className="h-10 w-10 md:h-16 md:w-16 rounded-full overflow-hidden">
                <img src={DEFAULT_PROFILE_PIC} alt="tutor_profile_pic" className="w-full h-full object-cover" />
              </div>
              <span className="text-2xl font-normal">{`Ayesha`}</span>
            </div>
            <div className="hidden md:flex justify-center divide-x">
              <div className="flex flex-col items-center px-1">
                <span className="text-sm font-medium">0</span>
                <span className="text-xs font-medium text-neutralGray2">Students</span>
              </div>
              <div className="flex flex-col items-center px-1">
                <span className="text-sm font-medium">0</span>
                <span className="text-xs font-medium text-neutralGray2">Attendance</span>
              </div>
              <div className="flex flex-col items-center px-1">
                <span className="text-sm font-medium">0</span>
                <span className="text-xs font-medium text-neutralGray2 whitespace-nowrap">Online Exp.</span>
              </div>
              <div className="flex flex-col items-center px-1">
                <span className="text-sm font-medium">0</span>
                <span className="text-xs font-medium text-neutralGray2 whitespace-nowrap">Offline Exp.</span>
              </div>
            </div>
            <div className="hidden md:flex justify-center flex-wrap gap-2 py-2">
              {categories.map((item: any, i: any) => (
                <span className="rounded-full border bg-white text-primary text-xs font-medium px-3 py-1" key={i}>
                  {item.name}
                </span>
              ))}
            </div>
            <div className="w-full flex items-center gap-2">
              <p className="text-sm text-neutralGray2">Tutor’s Payment/Session</p>
              <input
                type="number"
                className="max-w-[100px] py-2 px-2 bg-white rounded-md text-base text-neutralGray1"
              />
            </div>

            <div className="w-full max-w-sm mx-auto flex gap-4 items-center justify-center whitespace-nowrap">
              <Button block text="View Profile" type="secondary" />
            </div>
          </div>
        </div>
        <div className="w-full min-w-[300px] flex flex-col items-center border border-neutralGray3 p-4 rounded-lg cursor-pointer bg-neutralBlue2">
          <div className="w-full flex items-center justify-center">
            <p className="text-sm underline">New Assigned Tutor</p>
          </div>
          <div className="m-auto">
            <Button text="Assign new Tutor" />
          </div>
        </div>
      </div>
    </div>
  );
}
